import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { WorkflowSettingsBuilder } from 'src/entity/ProjectSettingsForm/ui/WorkflowSettingsBuilder';
import {
  buildEmptyWorkflowSettings,
  normalizeWorkflowSettingsForSave,
  validateWorkflowSettings,
} from 'src/entity/ProjectSettingsForm/ui/workflowSettings';
import { FieldType } from 'src/entity/ProjectSettingsForm/types';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import {
  DirectoryWorkflowSettings,
  FormDirectoryType,
  FormType,
} from 'src/shared/store/projects/types';
import { setError, setSuccess } from 'src/shared/lib/utils';
import { Button, Select, SelectItemType } from 'src/shared/ui';

interface ProjectSettingDirectoryFormProps {
  index: number;
  onSave: (i: number, data: FormDirectoryType) => void;
  onClose: () => void;
  directory?: FormDirectoryType;
}

type AttributeDraft = {
  columnName: string;
  columnLabel: string;
  columnType: string;
};

type HierarchyDraft = {
  valueId: string;
  parentValueId: string;
  sortOrder: string;
};

export const ProjectSettingDirectoryForm: FC<ProjectSettingDirectoryFormProps> = ({
  onSave,
  index,
  directory,
  onClose,
}) => {
  const { forms, selectedProject } = useContext(ProjectsContext);

  const [data, setData] = useState<FormDirectoryType | null>(directory || null);
  const [settings, setSettings] = useState<FieldType[]>([]);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [hierarchy, setHierarchy] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [projectDirectories, setProjectDirectories] = useState<FormType[]>([]);
  const [workflowSettings, setWorkflowSettings] = useState<DirectoryWorkflowSettings>(
    buildEmptyWorkflowSettings(),
  );

  const [attributeDraft, setAttributeDraft] = useState<AttributeDraft>({
    columnName: '',
    columnLabel: '',
    columnType: 'text',
  });
  const [hierarchyDraft, setHierarchyDraft] = useState<HierarchyDraft>({
    valueId: '',
    parentValueId: '',
    sortOrder: '0',
  });

  const directoryForms = useMemo(() => {
    if (projectDirectories.length) {
      return projectDirectories;
    }
    const onlyDirectories = forms.filter(form => form?.is_directory === true);
    return onlyDirectories.length ? onlyDirectories : forms;
  }, [forms, projectDirectories]);

  const formsList: SelectItemType[] = directoryForms.map(({ form_name, id }) => ({
    label: form_name,
    value: id.toString(),
  }));

  const syncDirectorySettings = async (directoryId: number) => {
    setIsLoading(true);
    try {
      const [{ data: tableData }, attributesResponse, hierarchyResponse, workflowResponse] =
        await Promise.all([
          ProjectsService.getTableSettings({ formId: directoryId }),
          ProjectsService.getDirectoryAttributes(directoryId).catch(() => null),
          ProjectsService.getDirectoryHierarchy(directoryId).catch(() => null),
          ProjectsService.getDirectoryWorkflow(directoryId).catch(() => null),
        ]);

      const list = tableData.FormStructure.filter(
        field => !(field.columnName !== 'id' && field.columnType === 'reference'),
      );

      setSettings(list);
      setAttributes(attributesResponse?.data?.items || []);
      setHierarchy(hierarchyResponse?.data?.items || []);
      setWorkflowSettings(
        workflowResponse?.data?.workflowSettings
          ? normalizeWorkflowSettingsForSave(workflowResponse.data.workflowSettings)
          : buildEmptyWorkflowSettings(),
      );

      setData(prev => ({
        referenceFormId: directoryId,
        referenceFormKey:
          prev?.referenceFormId === directoryId && prev.referenceFormKey
            ? prev.referenceFormKey
            : list?.[0]?.columnName,
        referenceFormValue:
          prev?.referenceFormId === directoryId && prev.referenceFormValue
            ? prev.referenceFormValue
            : list?.[0]?.columnName,
      }));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Не удалось загрузить настройки справочника');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedProject?.id) {
      setProjectDirectories([]);
      return;
    }

    (async () => {
      try {
        const { data: directoriesResponse } = await ProjectsService.getProjectDirectories(
          selectedProject.id,
        );
        const mapped: FormType[] = (directoriesResponse?.directories || []).map(item => ({
          id: item.id,
          form_name: item.alias || item.formName,
          form_description: item.formDescription || '',
          is_directory: true,
        }));
        setProjectDirectories(mapped);
      } catch {
        setProjectDirectories([]);
      }
    })();
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!directoryForms.length) {
      return;
    }
    const targetDirectoryId = directory?.referenceFormId || directoryForms[0].id;
    void syncDirectorySettings(targetDirectoryId);
  }, [directory?.referenceFormId, directoryForms]);

  const handleChangeTable = async (value: SelectItemType) => {
    await syncDirectorySettings(Number(value.value));
  };

  const handleCreateAttribute = async () => {
    const directoryId = data?.referenceFormId;
    if (!directoryId) {
      return;
    }
    if (!attributeDraft.columnName.trim() || !attributeDraft.columnLabel.trim()) {
      setError('Поля columnName и columnLabel обязательны');
      return;
    }

    try {
      await ProjectsService.createDirectoryAttribute(directoryId, {
        columnName: attributeDraft.columnName.trim(),
        columnLabel: attributeDraft.columnLabel.trim(),
        columnType: attributeDraft.columnType,
      });
      setSuccess('Атрибут справочника создан');
      setAttributeDraft({ columnName: '', columnLabel: '', columnType: 'text' });
      await syncDirectorySettings(directoryId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Не удалось создать атрибут справочника');
    }
  };

  const handleDeleteAttribute = async (attributeId: number) => {
    const directoryId = data?.referenceFormId;
    if (!directoryId) {
      return;
    }
    try {
      await ProjectsService.deleteDirectoryAttribute(directoryId, attributeId);
      setSuccess('Атрибут справочника удалён');
      await syncDirectorySettings(directoryId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Не удалось удалить атрибут справочника');
    }
  };

  const handleCreateHierarchyItem = async () => {
    const directoryId = data?.referenceFormId;
    if (!directoryId) {
      return;
    }

    const valueId = Number(hierarchyDraft.valueId);
    const parentValueId = hierarchyDraft.parentValueId.trim()
      ? Number(hierarchyDraft.parentValueId)
      : undefined;
    const sortOrder = Number(hierarchyDraft.sortOrder || 0);

    if (!Number.isFinite(valueId) || valueId <= 0) {
      setError('valueId в иерархии должен быть положительным числом');
      return;
    }
    if (parentValueId !== undefined && (!Number.isFinite(parentValueId) || parentValueId <= 0)) {
      setError('parentValueId должен быть пустым или положительным числом');
      return;
    }

    try {
      await ProjectsService.createDirectoryHierarchyItem(directoryId, {
        valueId,
        ...(parentValueId ? { parentValueId } : {}),
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
      });
      setSuccess('Элемент иерархии создан');
      setHierarchyDraft({ valueId: '', parentValueId: '', sortOrder: '0' });
      await syncDirectorySettings(directoryId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Не удалось создать элемент иерархии');
    }
  };

  const handleDeleteHierarchyItem = async (itemId: number) => {
    const directoryId = data?.referenceFormId;
    if (!directoryId) {
      return;
    }
    try {
      await ProjectsService.deleteDirectoryHierarchyItem(directoryId, itemId);
      setSuccess('Элемент иерархии удалён');
      await syncDirectorySettings(directoryId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Не удалось удалить элемент иерархии');
    }
  };

  const handleSave = async () => {
    if (!data?.referenceFormId) {
      return;
    }

    const parsedWorkflowSettings = normalizeWorkflowSettingsForSave(workflowSettings);
    const workflowErrors = validateWorkflowSettings(parsedWorkflowSettings);
    if (workflowErrors.length) {
      setError(`Проверка бизнес-процесса справочника: ${workflowErrors[0]}`);
      return;
    }

    setIsLoading(true);
    try {
      await ProjectsService.upsertDirectoryWorkflow(data.referenceFormId, {
        workflowSettings: parsedWorkflowSettings,
      });
      onSave(index, data);
      setSuccess('Привязка и бизнес-процесс справочника сохранены');
    } catch (e: any) {
      setError(
        e?.response?.data?.error || 'Не удалось сохранить настройки справочника и бизнес-процесса',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const canSaveBinding = Boolean(directoryForms.length && data?.referenceFormId);

  return (
    <>
      <div className="font-gothampro-500 mb-6 text-center text-xl dark:text-white">
        Привязка справочника
      </div>

      {directoryForms.length ? (
        <div className="mb-8 grid w-[44rem] auto-rows-[44px] grid-cols-3 gap-4">
          <div className="font-gothampro-400 flex items-center justify-center text-sm dark:text-white">
            Справочник
          </div>
          <div className="font-gothampro-400 flex items-center justify-center text-sm dark:text-white">
            Ключевая колонка
          </div>
          <div className="font-gothampro-400 flex items-center justify-center text-sm dark:text-white">
            Колонка значения
          </div>

          <Select
            classNames=""
            value={data?.referenceFormId?.toString()}
            options={formsList}
            onChange={handleChangeTable}
          />

          {settings.length ? (
            <>
              <Select
                classNames=""
                value={data?.referenceFormKey}
                options={settings.map(({ columnName, columnLabel }) => ({
                  value: columnName,
                  label: columnLabel || columnName,
                }))}
                onChange={({ value }) => {
                  setData(prev => ({ ...prev, referenceFormKey: value }));
                }}
              />
              <Select
                classNames=""
                value={data?.referenceFormValue}
                options={settings.map(({ columnName, columnLabel }) => ({
                  value: columnName,
                  label: columnLabel || columnName,
                }))}
                onChange={({ value }) => {
                  setData(prev => ({ ...prev, referenceFormValue: value }));
                }}
              />
            </>
          ) : null}
        </div>
      ) : (
        <div className="font-gothampro-400 mb-8 text-center text-white">
          В выбранном проекте нет справочников
        </div>
      )}

      {!!data?.referenceFormId && (
        <div className="mb-8 w-[44rem] rounded border border-blue-400 p-3">
          <div className="mb-2 text-sm dark:text-white">Атрибуты справочника</div>
          <div className="mb-3 grid grid-cols-12 gap-2">
            <input
              value={attributeDraft.columnName}
              onChange={event =>
                setAttributeDraft(prev => ({ ...prev, columnName: event.target.value }))
              }
              placeholder="Имя колонки (columnName)"
              className="col-span-3 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            />
            <input
              value={attributeDraft.columnLabel}
              onChange={event =>
                setAttributeDraft(prev => ({ ...prev, columnLabel: event.target.value }))
              }
              placeholder="Название колонки (columnLabel)"
              className="col-span-4 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            />
            <select
              value={attributeDraft.columnType}
              onChange={event =>
                setAttributeDraft(prev => ({ ...prev, columnType: event.target.value }))
              }
              className="col-span-3 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            >
              <option value="text">text</option>
              <option value="number">number</option>
              <option value="floatNumber">floatNumber</option>
              <option value="date">date</option>
              <option value="boolean">boolean</option>
            </select>
            <Button
              type="button"
              variant="green"
              size="small"
              classNames="col-span-2"
              onClick={handleCreateAttribute}
            >
              Добавить
            </Button>
          </div>

          <div className="max-h-36 overflow-y-auto">
            {attributes.map(attribute => (
              <div
                key={attribute.ID}
                className="mb-1 grid grid-cols-12 items-center gap-2 rounded border border-violet-300 px-2 py-1 text-xs dark:border-violet-900 dark:text-white"
              >
                <div className="col-span-3">{attribute.ColumnName}</div>
                <div className="col-span-4">{attribute.ColumnLabel}</div>
                <div className="col-span-3">{attribute.ColumnType}</div>
                <Button
                  type="button"
                  variant="greyLight"
                  size="small"
                  classNames="col-span-2"
                  onClick={() => handleDeleteAttribute(attribute.ID)}
                >
                  Удалить
                </Button>
              </div>
            ))}

            {!attributes.length && (
              <div className="text-xs text-grey-300">Атрибуты не настроены</div>
            )}
          </div>
        </div>
      )}

      {!!data?.referenceFormId && (
        <div className="mb-10 w-[44rem] rounded border border-blue-400 p-3">
          <div className="mb-2 text-sm dark:text-white">Иерархия справочника</div>
          <div className="mb-3 grid grid-cols-12 gap-2">
            <input
              value={hierarchyDraft.valueId}
              onChange={event =>
                setHierarchyDraft(prev => ({ ...prev, valueId: event.target.value }))
              }
              placeholder="ID значения (valueId)"
              className="col-span-3 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            />
            <input
              value={hierarchyDraft.parentValueId}
              onChange={event =>
                setHierarchyDraft(prev => ({ ...prev, parentValueId: event.target.value }))
              }
              placeholder="ID родителя (parentValueId, опционально)"
              className="col-span-4 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            />
            <input
              value={hierarchyDraft.sortOrder}
              onChange={event =>
                setHierarchyDraft(prev => ({ ...prev, sortOrder: event.target.value }))
              }
              placeholder="Порядок сортировки"
              className="col-span-3 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            />
            <Button
              type="button"
              variant="green"
              size="small"
              classNames="col-span-2"
              onClick={handleCreateHierarchyItem}
            >
              Добавить
            </Button>
          </div>

          <div className="max-h-36 overflow-y-auto">
            {hierarchy.map(item => (
              <div
                key={item.ID}
                className="mb-1 grid grid-cols-12 items-center gap-2 rounded border border-violet-300 px-2 py-1 text-xs dark:border-violet-900 dark:text-white"
              >
                <div className="col-span-3">значение: {item.ValueID}</div>
                <div className="col-span-4">родитель: {item.ParentValueID || '-'}</div>
                <div className="col-span-3">сортировка: {item.SortOrder}</div>
                <Button
                  type="button"
                  variant="greyLight"
                  size="small"
                  classNames="col-span-2"
                  onClick={() => handleDeleteHierarchyItem(item.ID)}
                >
                  Удалить
                </Button>
              </div>
            ))}

            {!hierarchy.length && (
              <div className="text-xs text-grey-300">Иерархия не настроена</div>
            )}
          </div>
        </div>
      )}

      {!!data?.referenceFormId && (
        <div className="mb-10 w-[44rem]">
          <WorkflowSettingsBuilder value={workflowSettings} onChange={setWorkflowSettings} />
        </div>
      )}

      <div className="flex justify-center gap-2.5">
        {canSaveBinding && data ? (
          <Button
            variant="green"
            classNames="w-52"
            disabled={isLoading}
            onClick={() => void handleSave()}
          >
            Сохранить
          </Button>
        ) : null}
        <Button variant="greyLight" classNames="w-52" onClick={onClose}>
          Отмена
        </Button>
      </div>
    </>
  );
};
